import type { RecurringItem } from '@/api/recurring'
import { formatShortDate } from '@/lib/formatDate'
import { shareSummary } from '@/lib/recurring'

export type DueTone = 'error' | 'warning' | 'primary'

const STRIP_DAYS = 7

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

function daysFrom(nextDueDate: string, today: Date): number {
  const [y, m, d] = nextDueDate.split('-').map(Number)
  return Math.round((new Date(y, m - 1, d).getTime() - startOfDay(today)) / 86_400_000)
}

/** The due badge for a row or card; null once it's more than a week away. */
export function dueBadge(
  nextDueDate: string,
  today: Date = new Date()
): { text: string; tone: DueTone } | null {
  const days = daysFrom(nextDueDate, today)
  if (days < 0) return { text: `${-days} day${days === -1 ? '' : 's'} late`, tone: 'error' }
  if (days === 0) return { text: 'Today', tone: 'warning' }
  if (days === 1) return { text: 'Tomorrow', tone: 'warning' }
  if (days <= STRIP_DAYS)
    return { text: `In ${days} days`, tone: days <= 2 ? 'warning' : 'primary' }
  return null
}

/** Due today or late — still waiting to be marked paid, received or missed. */
export function isDueNow(nextDueDate: string, today: Date = new Date()): boolean {
  return daysFrom(nextDueDate, today) <= 0
}

export function isInStrip(nextDueDate: string, today: Date = new Date()): boolean {
  return daysFrom(nextDueDate, today) <= STRIP_DAYS
}

export function nextUpLabel(nextDueDate: string, today: Date = new Date()): string {
  const days = daysFrom(nextDueDate, today)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days > 1 && days <= STRIP_DAYS) return `in ${days} days`
  return formatShortDate(nextDueDate)
}

/** Who or what pays it: "Paid by X", "Shared with X", or the account. */
export function cardSubline(item: RecurringItem): string | null {
  const share = shareSummary(item)
  if (share?.kind === 'paidBy') return `Paid by ${share.payer}`
  if (share?.kind === 'chipIn') return `Shared with ${share.others[0]}`
  return item.financialAccountName ?? null
}
